import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AlertCircle,
  CheckCircle,
  Check,
  Clock,
  Copy,
  PauseCircle,
  MinusCircle,
} from 'react-feather';
import { AssistantEntry, AssistantTool } from './types';
import { MarkdownMessage } from './MarkdownMessage';
import { MessageAttachments } from './MessageAttachments';
import { EmailDraftAttachments } from './EmailDraftAttachments';
import { ShimmerText } from '../components/ShimmerText';

const ACTIVITY_SHIMMER_SECONDS = 2;

const ChatMessage = ({ entry }: { entry: AssistantEntry }) => {
  const message = entry.message!;
  const isUser = message.role === 'user';
  const timestamp = new Date(entry.createdAt);
  const hasTimestamp = !Number.isNaN(timestamp.getTime());
  const text = message.content.map((part) => part.text || '').join('\n\n');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>(
    'idle'
  );
  useEffect(() => {
    if (copyState === 'idle') return undefined;
    const timeout = setTimeout(() => setCopyState('idle'), 2000);
    return () => clearTimeout(timeout);
  }, [copyState]);
  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
  };
  const copyLabel = copyState === 'copied' ? 'Copied!' : 'Copy message';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        mb: isUser ? 1 : 3,
        mt: 1,
      }}
    >
      <Box
        sx={
          isUser
            ? {
                maxWidth: '90%',
                px: 1.5,
                py: 0.75,
                bgcolor: 'action.hover',
                border: 1,
                borderColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                borderRadius: '16px 16px 4px 16px',
              }
            : { width: '100%', minWidth: 0 }
        }
      >
        {isUser ? (
          message.content.map((part, index) => (
            <Typography
              // Content part positions are stable while streamed text grows.
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
                lineHeight: 1.65,
                '& + &': { mt: 1.5 },
              }}
            >
              {part.text}
            </Typography>
          ))
        ) : (
          <MarkdownMessage text={text} />
        )}
        <MessageAttachments message={message} />
        {message.status === 'incomplete' && (
          <Chip size="small" label="Incomplete" sx={{ mt: 1 }} />
        )}
      </Box>
      {(hasTimestamp || text.trim()) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          {hasTimestamp && (
            <Typography
              component="time"
              dateTime={timestamp.toISOString()}
              title={timestamp.toLocaleString()}
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 10 }}
            >
              {timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          )}
          {!!text.trim() && (
            <Tooltip title={copyLabel}>
              <IconButton
                aria-label={copyLabel}
                size="small"
                onClick={copyMessage}
                sx={{
                  color: 'text.secondary',
                  width: { xs: 32, sm: 24 },
                  height: { xs: 32, sm: 24 },
                }}
              >
                {copyState === 'copied' ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </IconButton>
            </Tooltip>
          )}
          <Box
            component="span"
            aria-live="polite"
            sx={{
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {copyState === 'copied' ? 'Message copied' : ''}
          </Box>
          {copyState === 'error' && (
            <Typography variant="caption" color="error" aria-live="polite">
              Couldn’t copy. Try again.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export const toolLabel = (tool: AssistantTool) => {
  if (tool.status === 'waiting') return 'Waiting for your input';
  if (['pending', 'running'].includes(tool.status))
    return tool.presentation.running || 'Working…';
  if (tool.status === 'completed')
    return tool.presentation.completed || 'Completed';
  return (
    tool.presentation.statuses?.[tool.status] ||
    tool.presentation.failed ||
    'Action did not complete'
  );
};

const ToolStatusIcon = ({ status }: { status: string }) => {
  const running = status === 'running';
  let StatusIcon = AlertCircle;
  let color = 'error.main';
  if (status === 'pending') {
    StatusIcon = Clock;
    color = 'text.secondary';
  } else if (status === 'waiting') {
    StatusIcon = PauseCircle;
    color = 'primary.main';
  } else if (status === 'completed') {
    StatusIcon = CheckCircle;
    color = 'success.main';
  } else if (['cancelled', 'declined'].includes(status)) {
    StatusIcon = MinusCircle;
    color = 'text.secondary';
  }
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        color,
        width: 18,
        height: 18,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {running ? (
        <>
          <CircularProgress
            variant="determinate"
            value={100}
            size={14}
            thickness={4}
            sx={{ color: 'action.hover', position: 'absolute' }}
          />
          <CircularProgress
            size={14}
            thickness={4}
            sx={{
              color: 'primary.main',
              '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
              '@media (prefers-reduced-motion: reduce)': {
                animation: 'none',
                '& .MuiCircularProgress-circle': { animation: 'none' },
              },
            }}
          />
        </>
      ) : (
        <StatusIcon size={14} strokeWidth={1.75} />
      )}
    </Box>
  );
};

export const mergeTimeline = (
  existing: AssistantEntry[],
  incoming: AssistantEntry[]
) => {
  const entries = new Map(existing.map((entry) => [entry.id, entry]));
  incoming.forEach((entry) => entries.set(entry.id, entry));
  return [...entries.values()].sort((a, b) => a.sequence - b.sequence);
};

export interface AssistantActivityState {
  run?: { id: string; status: string };
  pending?: boolean;
  connected: boolean;
  interrupted?: boolean;
}

const hasPersistentResult = (tool: AssistantTool) =>
  !!(
    tool.draft ||
    tool.action ||
    tool.retryable ||
    tool.status === 'waiting' ||
    !['pending', 'running', 'completed'].includes(tool.status)
  );

const currentActivity = (
  entries: AssistantEntry[],
  tools: Map<string, AssistantTool>,
  activity?: AssistantActivityState
): string | undefined => {
  if (!activity || activity.interrupted) return undefined;
  if (activity.pending) return 'Working…';
  const { run } = activity;
  if (
    !activity.connected ||
    !run ||
    !['queued', 'running'].includes(run.status)
  )
    return undefined;

  const runEntries = entries.filter((entry) => entry.runId === run.id);
  const anchors = new Map<string, AssistantEntry>();
  runEntries.forEach((entry) => {
    if (entry.tool && !anchors.has(entry.tool.id))
      anchors.set(entry.tool.id, entry);
  });
  const visibleTools = [...anchors.values()]
    .map((entry) => ({ entry, tool: tools.get(entry.tool!.id)! }))
    .filter(({ tool }) => tool.presentation.visible !== false);
  if (visibleTools.some(({ tool }) => tool.status === 'waiting' || tool.action))
    return undefined;

  const executing = visibleTools
    .filter(({ tool }) => ['running', 'pending'].includes(tool.status))
    .sort((a, b) => {
      // Prefer execution over queued work, then the most recently started call.
      const executingFirst =
        Number(a.tool.status === 'running') -
        Number(b.tool.status === 'running');
      const timeA = Date.parse(a.tool.startedAt || '');
      const timeB = Date.parse(b.tool.startedAt || '');
      return (
        executingFirst ||
        (Number.isFinite(timeA) && Number.isFinite(timeB) && timeA - timeB) ||
        a.entry.sequence - b.entry.sequence
      );
    })
    .pop()?.tool;
  if (executing) return toolLabel(executing);

  const writing = runEntries.some(
    ({ message }) =>
      message?.role === 'assistant' &&
      message.status === 'streaming' &&
      message.content.some((part) => part.text?.trim())
  );
  return writing ? 'Writing response…' : 'Working…';
};

export const AssistantTimeline = ({
  entries,
  activity = undefined,
  admin = false,
  disabled = false,
  onInsert = undefined,
  onAction = undefined,
  onRetryAction = undefined,
}: {
  entries: AssistantEntry[];
  activity?: AssistantActivityState;
  admin?: boolean;
  disabled?: boolean;
  onInsert?: (tool: AssistantTool) => void;
  onAction?: (tool: AssistantTool) => void;
  onRetryAction?: (tool: AssistantTool) => void;
}) => {
  const timeline = admin ? entries : mergeTimeline([], entries);
  const starts = new Set(
    timeline
      .filter((entry) => entry.kind === 'tool_call')
      .map((entry) => entry.tool?.id)
  );
  const toolStates = new Map(
    timeline
      .filter((entry) => entry.tool)
      .map((entry) => [entry.tool!.id, entry.tool!])
  );
  const progress = admin
    ? undefined
    : currentActivity(timeline, toolStates, activity);
  return (
    <Box aria-live={admin ? 'off' : 'polite'}>
      {timeline.map((entry) => {
        if (entry.message && !admin)
          return <ChatMessage key={entry.id} entry={entry} />;
        if (entry.message)
          return (
            <Paper
              variant="outlined"
              key={entry.id}
              sx={{
                p: 2,
                mb: 1,
                bgcolor:
                  entry.message.role === 'user'
                    ? 'action.hover'
                    : 'background.paper',
              }}
            >
              <Typography variant="caption">
                {entry.message.role === 'user' ? 'User' : 'Assistant'}
              </Typography>
              {entry.message.role !== 'assistant' ? (
                entry.message.content.map((part, index) => (
                  <Typography
                    // Content part positions are stable while streamed text grows.
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                  >
                    {part.text}
                  </Typography>
                ))
              ) : (
                <MarkdownMessage
                  text={entry.message.content
                    .map((part) => part.text || '')
                    .join('\n\n')}
                />
              )}
              <MessageAttachments message={entry.message} />
              {entry.message.status === 'incomplete' && (
                <Chip size="small" label="Incomplete" />
              )}
            </Paper>
          );
        const tool = entry.tool && toolStates.get(entry.tool.id);
        if (tool) {
          if (
            !admin &&
            (tool.presentation.visible === false ||
              !hasPersistentResult(tool) ||
              (entry.kind === 'tool_result' && starts.has(tool.id)))
          )
            return null;
          const running = ['pending', 'running', 'waiting'].includes(
            tool.status
          );
          const showToolStatus =
            admin || !['pending', 'running', 'completed'].includes(tool.status);
          return (
            <Paper
              variant={admin ? 'outlined' : 'elevation'}
              elevation={0}
              key={admin ? entry.id : tool.id}
              sx={
                admin
                  ? { p: 1.5, mb: 1 }
                  : { py: 0.5, mb: 0, bgcolor: 'transparent' }
              }
            >
              {showToolStatus && (
                <Box display="flex" alignItems="center" gap={1}>
                  {admin ? (
                    running && <CircularProgress size={14} />
                  ) : (
                    <ToolStatusIcon status={tool.status} />
                  )}
                  <Typography
                    variant="body2"
                    sx={
                      admin
                        ? undefined
                        : {
                            fontWeight: 500,
                            lineHeight: 1.5,
                            overflowWrap: 'anywhere',
                            color: running ? 'primary.main' : 'text.primary',
                            transition: 'color 180ms ease',
                          }
                    }
                  >
                    {admin
                      ? `${
                          entry.kind === 'tool_result'
                            ? 'Tool output'
                            : 'Tool call'
                        }: ${tool.name}`
                      : toolLabel(tool)}
                  </Typography>
                </Box>
              )}
              {admin && (
                <>
                  <Chip size="small" label={tool.status} />
                  <Typography variant="caption">
                    {' '}
                    {tool.durationMs != null
                      ? `${tool.durationMs} ms`
                      : ''} · {entry.createdAt}
                  </Typography>
                  <details>
                    <summary>Arguments, output, and execution details</summary>
                    <Box
                      component="pre"
                      sx={{
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        fontSize: 12,
                      }}
                    >
                      {JSON.stringify(tool, null, 2)}
                    </Box>
                  </details>
                </>
              )}
              {!admin && tool.draft && (
                <Box
                  sx={{
                    ml: showToolStatus ? 3.25 : 0,
                    mt: showToolStatus ? 1.5 : 0,
                  }}
                >
                  <Typography fontWeight="bold">
                    {tool.draft.subject}
                  </Typography>
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                      my: 1,
                    }}
                  >
                    {tool.draft.body}
                  </Typography>
                  <EmailDraftAttachments attachments={tool.draft.attachments} />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={() => onInsert?.(tool)}
                  >
                    Insert into email
                  </Button>
                </Box>
              )}
              {!admin && tool.retryable && (
                <Button
                  size="small"
                  disabled={disabled}
                  onClick={() => onRetryAction?.(tool)}
                  sx={{ ml: 3.25, mt: 0.5 }}
                >
                  Retry action
                </Button>
              )}
              {!admin && tool.action && (
                <Button
                  size="small"
                  onClick={() => onAction?.(tool)}
                  sx={{ ml: 3.25, mt: 0.5 }}
                >
                  Continue action
                </Button>
              )}
            </Paper>
          );
        }
        return entry.data?.message ? (
          <Typography
            key={entry.id}
            variant="body2"
            color="text.secondary"
            sx={{ my: 1 }}
          >
            {entry.data.message}
          </Typography>
        ) : null;
      })}
      {progress && (
        <Box
          key="activity"
          role="status"
          aria-atomic="true"
          sx={{ py: 0.5, color: 'text.secondary' }}
        >
          <ShimmerText variant="body2" cycleSeconds={ACTIVITY_SHIMMER_SECONDS}>
            {progress}
          </ShimmerText>
        </Box>
      )}
    </Box>
  );
};
